/* global gdAlertify */
import React, { useState, useRef } from "react";
import axiosInstance from "../config/axiosConfig";
import { formatDate, formatMoney } from "../utils/utils";
import GdTable from "./components/GdTable";
import MonthInput from "./components/MonthInput";

const mesActual = () => {
    const hoy = new Date();
    return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}`;
};

const monthNames = ["", "enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

const formatearMes = (periodoStr) => {
    if (!periodoStr) return "";
    const [year, month] = periodoStr.split("-");
    const nombreMes = monthNames[parseInt(month, 10)] || "";
    return `${nombreMes.charAt(0).toUpperCase()}${nombreMes.slice(1)} ${year}`;
};

// Email de notificación: tutor de pago -> tutor de salud -> primer correo de
// "Correos de notificación" del paciente. Un solo email (ya resuelto en el
// backend, ver AcuerdoMutuoController::calcularCobranzaFacturasPeriodo).
const emailNotificacionPaciente = (row = {}) => row.email_notificacion || "";

// Fila con datos de facturación incompletos (RUT/Razón social/Dirección/Comuna
// vacíos, sea porque el paciente no tiene datos_facturacion o porque a su
// propia ficha le falta algo). Se resalta la fila y se avisa antes de exportar.
const datosFacturacionIncompletos = (row = {}) => (
    !String(row.fac_rut || "").trim()
    || !String(row.fac_razon_social || "").trim()
    || !String(row.fac_direccion || "").trim()
    || !String(row.fac_comuna || "").trim()
);

const ReporteFacturacionPeriodo = () => {
    const [fecha, setFecha] = useState(mesActual());
    const [resultados, setResultados] = useState([]);
    const [loading, setLoading] = useState(false);
    const [buscado, setBuscado] = useState(false);
    const [showModalIncompletos, setShowModalIncompletos] = useState(false);
    const [pacientesIncompletos, setPacientesIncompletos] = useState([]);
    const gdTableRef = useRef(null);

    const buscar = async () => {
        if (!fecha) {
            gdAlertify.notiError("Debe indicar el periodo.");
            return;
        }

        setLoading(true);
        setBuscado(true);
        try {
            const response = await axiosInstance.get("/acuerdo-mutuo/cobranza/facturas", {
                params: { fecha, solo_servicios_periodo: 1 }
            });
            // Solo pacientes con monto a facturar: los de $0 no van en el reporte.
            setResultados((response.data.data || []).filter(row => Number(row.total_cobrar) > 0));
        } catch (error) {
            console.error("Error al buscar facturación del periodo:", error);
            gdAlertify.notiError("Ocurrió un error al cargar el reporte.");
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        { title: "RUT", name: "rut" },
        { title: "Paciente", name: "nombre_paciente" },
        {
            title: "Estado",
            name: "estado_paciente",
            render: row => (
                <div className="d-flex flex-column align-items-start">
                    {row.estado_paciente == 1 ? (
                        <span className="badge bg-success">Activo</span>
                    ) : (
                        <span className="badge bg-danger">Retirado</span>
                    )}
                    {row.estado_paciente != 1 && row.fecha_retiro && (
                        <small className="text-muted mt-1" style={{ fontSize: "0.75rem" }}>
                            Retiro: {formatDate(row.fecha_retiro)}
                        </small>
                    )}
                </div>
            )
        },
        {
            title: `Facturar ${formatearMes(fecha)}`,
            name: "total_cobrar",
            type: "number",
            render: row => <span className="fw-bold fs-6">{`$${formatMoney(row.total_cobrar)}`}</span>
        },
        { title: "N° Turnos", name: "cantidad_turnos", type: "number" },
        {
            title: "Total Turno",
            name: "total_turno",
            type: "number",
            render: row => {
                const total = (Number(row.valor_turnos_total) || 0) +
                              (Number(row.adicionales_turno_total) || 0) +
                              (Number(row.recargo_fs_turno_total) || 0);
                return `$${formatMoney(total)}`;
            }
        },
        {
            title: "Total Colación",
            name: "total_colacion",
            type: "number",
            render: row => {
                const total = (Number(row.colacion_total) || 0) +
                              (Number(row.adicionales_colacion_total) || 0) +
                              (Number(row.recargo_fs_colacion_total) || 0);
                return `$${formatMoney(total)}`;
            }
        },
        {
            title: "Total Movilización",
            name: "total_locomocion",
            type: "number",
            render: row => {
                const total = (Number(row.locomocion_total) || 0) +
                              (Number(row.adicionales_locomocion_total) || 0) +
                              (Number(row.recargo_fs_locomocion_total) || 0);
                return `$${formatMoney(total)}`;
            }
        },
        {
            title: "Otros / Extras",
            name: "total_otros",
            type: "number",
            render: row => {
                const total = (Number(row.otro_total) || 0) +
                              (Number(row.adicionales_otro_total) || 0) +
                              (Number(row.recargo_fs_otro_total) || 0) +
                              (Number(row.horas_extras_total) || 0) +
                              (Number(row.adicionales_horas_extras_total) || 0);
                return `$${formatMoney(total)}`;
            }
        },
        { title: "Facturar a", name: "fac_facturar_a" },
        { title: "RUT Factura", name: "fac_rut" },
        { title: "Nombre/Razon Social Factura", name: "fac_razon_social", width: 220 },
        { title: "Dirección Factura", name: "fac_direccion", width: 220 },
        { title: "Comuna Factura", name: "fac_comuna" },
        { title: "Glosa Factura", name: "fac_giro", width: 220 },
        {
            title: "Email notificación Factura",
            name: "email_notificacion",
            width: 220,
            render: row => <span className="small text-secondary">{emailNotificacionPaciente(row) || "—"}</span>
        },
        {
            // Siguiente email de la misma cadena (tutor de pago -> tutor de salud ->
            // correos de notificación del paciente), distinto del primero.
            title: "2do Email notificación Factura",
            name: "email_notificacion_2",
            width: 220,
            render: row => <span className="small text-secondary">{row.email_notificacion_2 || "—"}</span>
        },
    ];

    const getRowClass = row => (datosFacturacionIncompletos(row) ? "fila-facturacion-incompleta" : "");

    // Se ejecuta al apretar "Exportar Datos". Si hay filas con datos de
    // facturación incompletos entre las que se van a exportar, cancela el
    // export y muestra el modal en vez de eso.
    const handleBeforeExport = dataAExportar => {
        const incompletos = dataAExportar.filter(datosFacturacionIncompletos);
        if (incompletos.length === 0) return true;

        setPacientesIncompletos(incompletos);
        setShowModalIncompletos(true);
        return false;
    };

    const exportarDeTodosModos = () => {
        setShowModalIncompletos(false);
        gdTableRef.current?.exportNow();
    };

    return (
        <div className="container-fluid mt-5">
            <div className="card p-4">
                <h3 className="mb-4">Facturación por Periodo</h3>
                <p className="text-muted small">
                    Detalle de facturación por paciente para un periodo: monto a facturar, desglose de turnos y
                    datos de facturación (a quién se factura, RUT, razón social, dirección, glosa y email).
                </p>
                <div className="row g-2 align-items-end mb-4">
                    <div className="col-md-3">
                        <label className="form-label">Periodo</label>
                        <MonthInput
                            className="form-control"
                            value={fecha}
                            onChange={(e) => setFecha(e.target.value)}
                        />
                    </div>
                    <div className="col-md-2">
                        <button className="btn btn-primary w-100" onClick={buscar} disabled={loading}>
                            {loading ? "Buscando..." : "Buscar"}
                        </button>
                    </div>
                </div>

                {resultados.some(datosFacturacionIncompletos) && (
                    <p className="small mb-2">
                        <i className="bi bi-square-fill me-1" style={{ color: "#fff3b0" }}></i>
                        Filas resaltadas: falta RUT, razón social, dirección o comuna de facturación.
                    </p>
                )}

                <style>{`.fila-facturacion-incompleta > td { background-color: #fff9db !important; }`}</style>

                <GdTable
                    ref={gdTableRef}
                    columns={columns}
                    data={resultados}
                    stickyHeader={true}
                    height={600}
                    headerHeight={50}
                    showCsvExport={true}
                    exportFilename={`facturacion_${fecha || "periodo"}.xlsx`}
                    loading={loading}
                    maxRows={500}
                    getRowClass={getRowClass}
                    onBeforeExport={handleBeforeExport}
                />

                {buscado && !loading && resultados.length === 0 && (
                    <p className="text-muted mt-3">No se encontraron datos para el periodo indicado.</p>
                )}
            </div>

            {showModalIncompletos && (
                <>
                    <div className="modal-backdrop fade show"></div>
                    <div className="modal fade show" style={{ display: "block" }} tabIndex="-1">
                        <div className="modal-dialog modal-dialog-scrollable">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Datos de facturación incompletos</h5>
                                    <button type="button" className="btn-close" onClick={() => setShowModalIncompletos(false)} />
                                </div>
                                <div className="modal-body">
                                    <p>
                                        A los siguientes {pacientesIncompletos.length} paciente(s) les falta RUT, razón social,
                                        dirección o comuna de facturación. Complétalos en la ficha del paciente (sección
                                        Datos Facturación) antes de exportar.
                                    </p>
                                    <ul className="list-group">
                                        {pacientesIncompletos.map(row => (
                                            <li key={row.paciente_id} className="list-group-item">
                                                <strong>{row.nombre_paciente}</strong> ({row.rut})
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowModalIncompletos(false)}>
                                        Cerrar
                                    </button>
                                    <button type="button" className="btn btn-warning" onClick={exportarDeTodosModos}>
                                        Exportar de todos modos
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default ReporteFacturacionPeriodo;
